import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
  customersAPI,
  businessTypesAPI,
  magazinesAPI,
  adSizesAPI,
  contentSizesAPI,
  bookingsAPI,
  dashboardAPI,
} from '../../utils/api';
import api from '../../utils/api';

export const fetchCustomers = createAsyncThunk(
  'booking/fetchCustomers',
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await customersAPI.getAll(params);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch customers');
    }
  }
);

export const createCustomer = createAsyncThunk(
  'booking/createCustomer',
  async (customerData, { rejectWithValue }) => {
    try {
      const response = await customersAPI.create(customerData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to create customer');
    }
  }
);

export const fetchBusinessTypes = createAsyncThunk(
  'booking/fetchBusinessTypes',
  async (includeArchived = false, { rejectWithValue }) => {
    try {
      const response = await businessTypesAPI.getAll(includeArchived);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch business types');
    }
  }
);

export const createBusinessType = createAsyncThunk(
  'booking/createBusinessType',
  async (businessTypeData, { rejectWithValue }) => {
    try {
      const response = await businessTypesAPI.create(businessTypeData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to create business type');
    }
  }
);

export const fetchMagazines = createAsyncThunk(
  'booking/fetchMagazines',
  async (includeArchived = false, { rejectWithValue }) => {
    try {
      const response = await magazinesAPI.getAll(includeArchived);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch magazines');
    }
  }
);

export const createMagazine = createAsyncThunk(
  'booking/createMagazine',
  async (magazineData, { rejectWithValue }) => {
    try {
      const response = await magazinesAPI.create(magazineData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to create magazine');
    }
  }
);

export const fetchAdSizes = createAsyncThunk(
  'booking/fetchAdSizes',
  async (_, { rejectWithValue }) => {
    try {
      const response = await adSizesAPI.getAll();
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch ad sizes');
    }
  }
);

export const fetchContentSizes = createAsyncThunk(
  'booking/fetchContentSizes',
  async (includeArchived = false, { rejectWithValue }) => {
    try {
      const response = await contentSizesAPI.getAll(includeArchived);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch content sizes');
    }
  }
);

export const createContentSize = createAsyncThunk(
  'booking/createContentSize',
  async (contentSizeData, { rejectWithValue }) => {
    try {
      const response = await contentSizesAPI.create(contentSizeData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to create content size');
    }
  }
);

export const fetchBookings = createAsyncThunk(
  'booking/fetchBookings',
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await bookingsAPI.getAll(params);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch bookings');
    }
  }
);

export const createBooking = createAsyncThunk(
  'booking/createBooking',
  async (bookingData, { rejectWithValue }) => {
    try {
      const response = await bookingsAPI.create(bookingData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to create booking');
    }
  }
);

export const fetchDashboardStats = createAsyncThunk(
  'booking/fetchDashboardStats',
  async (_, { rejectWithValue }) => {
    try {
      const response = await dashboardAPI.getStats();
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch dashboard stats');
    }
  }
);

export const fetchCurrentIssueData = createAsyncThunk(
  'booking/fetchCurrentIssueData',
  async (magazineId, { rejectWithValue }) => {
    try {
      const response = await dashboardAPI.getCurrentIssue(magazineId);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch current issue data');
    }
  }
);

export const fetchPublications = createAsyncThunk(
  'booking/fetchPublications',
  async (_, { rejectWithValue }) => {
    try {
      const response = await dashboardAPI.getPublications();
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch publications');
    }
  }
);

export const deleteBookingAsync = createAsyncThunk(
  'booking/deleteBookingAsync',
  async (bookingId, { rejectWithValue }) => {
    try {
      await api.delete(`/bookings/${bookingId}`);
      return bookingId;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to delete booking');
    }
  }
);

export const updateBookingAsync = createAsyncThunk(
  'booking/updateBookingAsync',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await bookingsAPI.update(id, data);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update booking');
    }
  }
);

// Diff-submit for the per-customer edit page.
// Payload: { customer, notes, create, update, deleteIds }
// Response: { created, updated, deletedIds }
export const syncCustomerBookingsAsync = createAsyncThunk(
  'booking/syncCustomerBookings',
  async (payload, { rejectWithValue }) => {
    try {
      const response = await bookingsAPI.sync(payload);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to sync bookings');
    }
  }
);

const initialState = {
  customers: [],
  customersPagination: {
    currentPage: 1,
    totalPages: 1,
    totalCustomers: 0,
    hasNextPage: false,
    hasPrevPage: false,
    limit: 20,
  },
  businessTypes: [],
  magazines: [],
  adSizes: [],
  contentSizes: [],
  bookings: [],
  leafletDeliveries: [],
  dashboardStats: {},
  currentIssueData: null,
  publications: [],
  loading: {
    customers: false,
    businessTypes: false,
    magazines: false,
    adSizes: false,
    contentSizes: false,
    bookings: false,
    leafletDeliveries: false,
    dashboard: false,
  },
  error: null,
};

const bookingSlice = createSlice({
  name: 'booking',
  initialState,
  reducers: {
    clearError: state => {
      state.error = null;
    },
    updateBooking: (state, action) => {
      const index = state.bookings.findIndex(b => b._id === action.payload._id);
      if (index !== -1) {
        state.bookings[index] = action.payload;
      }
    },
    deleteBooking: (state, action) => {
      state.bookings = state.bookings.filter(b => b._id !== action.payload);
    },
    updateCustomer: (state, action) => {
      const index = state.customers.findIndex(c => c._id === action.payload._id);
      if (index !== -1) {
        state.customers[index] = action.payload;
      }
    },
    deleteCustomer: (state, action) => {
      state.customers = state.customers.filter(c => c._id !== action.payload);
    },
    addCustomer: (state, action) => {
      state.customers.push(action.payload);
    },
    updateBusinessType: (state, action) => {
      const index = state.businessTypes.findIndex(bt => bt._id === action.payload._id);
      if (index !== -1) {
        state.businessTypes[index] = action.payload;
      }
    },
    deleteBusinessType: (state, action) => {
      state.businessTypes = state.businessTypes.filter(bt => bt._id !== action.payload);
    },
    addBusinessType: (state, action) => {
      state.businessTypes.push(action.payload);
    },
    updateMagazine: (state, action) => {
      const index = state.magazines.findIndex(m => m._id === action.payload._id);
      if (index !== -1) {
        state.magazines[index] = action.payload;
      }
    },
    deleteMagazine: (state, action) => {
      state.magazines = state.magazines.filter(m => m._id !== action.payload);
    },
    addMagazine: (state, action) => {
      state.magazines.push(action.payload);
    },
    updateContentSize: (state, action) => {
      const index = state.contentSizes.findIndex(cs => cs._id === action.payload._id);
      if (index !== -1) {
        state.contentSizes[index] = action.payload;
        // Re-sort after updating
        state.contentSizes.sort((a, b) => {
          const aValue = a.adSize?.decimalValue || 0;
          const bValue = b.adSize?.decimalValue || 0;
          return aValue - bValue;
        });
      }
    },
    deleteContentSize: (state, action) => {
      state.contentSizes = state.contentSizes.filter(cs => cs._id !== action.payload);
    },
    addContentSize: (state, action) => {
      state.contentSizes.push(action.payload);
      // Re-sort after adding
      state.contentSizes.sort((a, b) => {
        const aValue = a.adSize?.decimalValue || 0;
        const bValue = b.adSize?.decimalValue || 0;
        return aValue - bValue;
      });
    },
  },
  extraReducers: builder => {
    builder
      .addCase(fetchCustomers.pending, state => {
        state.loading.customers = true;
        state.error = null;
      })
      .addCase(fetchCustomers.fulfilled, (state, action) => {
        state.loading.customers = false;
        if (action.payload.customers) {
          state.customers = action.payload.customers;
          state.customersPagination = action.payload.pagination;
        } else {
          state.customers = action.payload;
        }
      })
      .addCase(fetchCustomers.rejected, (state, action) => {
        state.loading.customers = false;
        state.error = action.payload;
      })
      .addCase(createCustomer.fulfilled, (state, action) => {
        state.customers.push(action.payload);
      })

      .addCase(fetchBusinessTypes.pending, state => {
        state.loading.businessTypes = true;
        state.error = null;
      })
      .addCase(fetchBusinessTypes.fulfilled, (state, action) => {
        state.loading.businessTypes = false;
        state.businessTypes = action.payload;
      })
      .addCase(fetchBusinessTypes.rejected, (state, action) => {
        state.loading.businessTypes = false;
        state.error = action.payload;
      })
      .addCase(createBusinessType.fulfilled, (state, action) => {
        state.businessTypes.push(action.payload);
      })

      .addCase(fetchMagazines.pending, state => {
        state.loading.magazines = true;
        state.error = null;
      })
      .addCase(fetchMagazines.fulfilled, (state, action) => {
        state.loading.magazines = false;
        state.magazines = action.payload;
      })
      .addCase(fetchMagazines.rejected, (state, action) => {
        state.loading.magazines = false;
        state.error = action.payload;
      })
      .addCase(createMagazine.fulfilled, (state, action) => {
        state.magazines.push(action.payload);
      })

      .addCase(fetchAdSizes.pending, state => {
        state.loading.adSizes = true;
        state.error = null;
      })
      .addCase(fetchAdSizes.fulfilled, (state, action) => {
        state.loading.adSizes = false;
        state.adSizes = action.payload;
      })
      .addCase(fetchAdSizes.rejected, (state, action) => {
        state.loading.adSizes = false;
        state.error = action.payload;
      })

      .addCase(fetchContentSizes.pending, state => {
        state.loading.contentSizes = true;
        state.error = null;
      })
      .addCase(fetchContentSizes.fulfilled, (state, action) => {
        state.loading.contentSizes = false;
        // Sort content sizes by decimal value (smallest to largest)
        state.contentSizes = action.payload.sort((a, b) => {
          const aValue = a.adSize?.decimalValue || 0;
          const bValue = b.adSize?.decimalValue || 0;
          return aValue - bValue;
        });
      })
      .addCase(fetchContentSizes.rejected, (state, action) => {
        state.loading.contentSizes = false;
        state.error = action.payload;
      })
      .addCase(createContentSize.fulfilled, (state, action) => {
        state.contentSizes.push(action.payload);
        // Re-sort after adding new content size
        state.contentSizes.sort((a, b) => {
          const aValue = a.adSize?.decimalValue || 0;
          const bValue = b.adSize?.decimalValue || 0;
          return aValue - bValue;
        });
      })

      .addCase(fetchBookings.pending, state => {
        state.loading.bookings = true;
        state.error = null;
      })
      .addCase(fetchBookings.fulfilled, (state, action) => {
        state.loading.bookings = false;
        state.bookings = action.payload;
      })
      .addCase(fetchBookings.rejected, (state, action) => {
        state.loading.bookings = false;
        state.error = action.payload;
      })
      .addCase(createBooking.fulfilled, (state, action) => {
        // Backend now returns { bookings: [...] } (one per row). Legacy single-object
        // responses are still handled for safety during the cutover.
        const created = Array.isArray(action.payload?.bookings)
          ? action.payload.bookings
          : [action.payload];
        created.forEach(b => state.bookings.unshift(b));
      })
      .addCase(updateBookingAsync.fulfilled, (state, action) => {
        const idx = state.bookings.findIndex(b => b._id === action.payload._id);
        if (idx !== -1) state.bookings[idx] = action.payload;
      })
      .addCase(syncCustomerBookingsAsync.fulfilled, (state, action) => {
        const { created = [], updated = [], deletedIds = [] } = action.payload || {};
        created.forEach(b => state.bookings.unshift(b));
        updated.forEach(b => {
          const idx = state.bookings.findIndex(x => x._id === b._id);
          if (idx !== -1) state.bookings[idx] = b;
        });
        if (deletedIds.length > 0) {
          state.bookings = state.bookings.filter(b => !deletedIds.includes(b._id));
        }
      })

      .addCase(fetchDashboardStats.pending, state => {
        state.loading.dashboard = true;
        state.error = null;
      })
      .addCase(fetchDashboardStats.fulfilled, (state, action) => {
        state.loading.dashboard = false;
        state.dashboardStats = action.payload;
      })
      .addCase(fetchDashboardStats.rejected, (state, action) => {
        state.loading.dashboard = false;
        state.error = action.payload;
      })
      .addCase(fetchCurrentIssueData.fulfilled, (state, action) => {
        state.currentIssueData = action.payload;
      })
      .addCase(fetchPublications.fulfilled, (state, action) => {
        state.publications = action.payload;
      })
      .addCase(deleteBookingAsync.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteBookingAsync.fulfilled, (state, action) => {
        state.loading = false;
        const bookingId = action.payload;
        state.bookings = state.bookings.filter(booking => booking._id !== bookingId);
      })
      .addCase(deleteBookingAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const {
  clearError,
  updateBooking,
  deleteBooking,
  updateCustomer,
  deleteCustomer,
  addCustomer,
  updateBusinessType,
  deleteBusinessType,
  addBusinessType,
  updateMagazine,
  deleteMagazine,
  addMagazine,
  updateContentSize,
  deleteContentSize,
  addContentSize,
} = bookingSlice.actions;

export default bookingSlice.reducer;
